import { format, isAfter, isValid, parseISO } from 'date-fns';
import React from 'react';
import { ScheduleAccordion } from '../../Situation';
import { type SituationWithDetails } from '../../Situation/Situation.types';
import { getDateStrings } from '../../Situation/SituationDateHelper';
import { TimeElement } from '../../TimeElement/TimeElement';
import { capitalizeFirstLetter } from '../../Util';
import formatCloseRelative from '../../Util/formatCloseRelative';
import { SeverityBadge } from '../../commonComponents';
import DescriptionList, { type DescriptionListRow } from './Partials/DescriptionList';
import PopupTemplateWrapper from './PopupTemplateWrapper';

export default function Situation(properties: SituationWithDetails): React.ReactElement {
    const currentSchedules = properties.scheduledOccurrences?.filter((o) => isAfter(parseISO(o.endTime), Date.now()));
    const startTimeISO = parseISO(properties.startTime);
    const datetexts = getDateStrings(startTimeISO, properties.endTime && parseISO(properties.endTime));
    const versionTimeISO = parseISO(properties.versionTime);
    const popupRows: DescriptionListRow[] = [
        {
            key: 'from',
            title: properties.workState ? 'Startar tidigast' : 'Giltig från',
            data: isValid(startTimeISO) ? (
                <TimeElement date={startTimeISO}>{capitalizeFirstLetter(datetexts.from)}</TimeElement>
            ) : (
                <>{capitalizeFirstLetter(datetexts.from)}</>
            ),
        },
    ];

    if (datetexts.to) {
        popupRows.push({
            key: 'to',
            title: 'Beräknas pågå till',
            data: isValid(parseISO(properties.endTime)) ? (
                <TimeElement date={parseISO(properties.endTime)}>{capitalizeFirstLetter(datetexts.to)}</TimeElement>
            ) : (
                <>{capitalizeFirstLetter(datetexts.to)}</>
            ),
        });
    }

    return (
        <PopupTemplateWrapper iconId={properties.iconId} title={properties.messageCode}>
            <div className="situation-popup">
                <h3 className="m-0">{properties.title}</h3>
                {!!properties.workState && <div className="fw-bold">{properties.workStateText}.</div>}
                <div>
                    {properties.isFuture && <span className="fw-bold">Kommande – </span>}
                    <span dangerouslySetInnerHTML={{ __html: properties.message }}></span>
                </div>
                {properties.additionalMessage && <div dangerouslySetInnerHTML={{ __html: properties.additionalMessage }}></div>}
                {properties.severityText && (
                    <div>
                        <SeverityBadge severity={properties.severity} severityText={properties.severityText} isFuture={properties.isFuture} />
                    </div>
                )}
                {currentSchedules?.length > 0 && (
                    <div>
                        <ScheduleAccordion timeSpans={properties.scheduledOccurrences} detailsUrl={properties.detailsPath} inline={true} />
                    </div>
                )}
                <DescriptionList rows={popupRows} />
                <p>
                    <a href={properties.detailsPath}>Läs mer och dela</a>
                </p>
                <dl className="popup-details">
                    <dt>Uppdaterad:</dt>
                    <dd>
                        <TimeElement date={versionTimeISO}>
                            {`${capitalizeFirstLetter(formatCloseRelative(versionTimeISO, 'noTime'))} ${format(versionTimeISO, 'HH.mm')}`}
                        </TimeElement>
                    </dd>
                    <dt>Källa:</dt>
                    <dd>{properties.provider}</dd>
                </dl>
            </div>
        </PopupTemplateWrapper>
    );
}
